import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import { consola } from 'consola/browser';
import { ActionIcon, Affix, Loader, Tooltip, Transition } from '@mantine/core';
import { SocketEvent } from '@/shared/libs/sockets/enums/socket-event';
import { IconCircleCheck } from '@tabler/icons-react';

const debug = process.env.NODE_ENV !== 'production';

type TSocketsContext = {
    connect(userId: string): void;
    disconnect(): void;
    subscribe(event: SocketEvent, callback: (data: any) => void): void;
    unsubscribe(event: SocketEvent, callback: (data: any) => void): void;
};

const SocketsContext = React.createContext<TSocketsContext>({} as TSocketsContext);

export const useSocketsContext = () => React.use(SocketsContext);

type TSocketsProps = {
    children?: ReactNode;
};

export const SocketsProvider = ({ children }: TSocketsProps) => {
    const ref = useRef<{
        socket: Socket | null;
        userId: string | null;
        isConnected: boolean;
    }>({
        socket: null,
        userId: null,
        isConnected: false,
    });
    
    // Хранилище всех активных подписок компонентов: Event -> Set<Callback>
    const listenersRef = useRef<Map<string, Set<(data: any) => void>>>(new Map());
    
    const [ connectionLost, setConnectionLost ] = useState(false);
    const [ showedLoader, setShowedLoader ] = useState(false);
    
    // Функция, которая привязывает все сохраненные подписки к текущему сокету
    const reattachListeners = useCallback(() => {
        const socket = ref.current.socket;
        if (!socket) return;
        
        listenersRef.current.forEach((callbacks, event) => {
            callbacks.forEach((cb) => {
                socket.off(event, cb); // Защита от дубликатов
                socket.on(event, cb);
            });
        });
    }, []);
    
    const bindEvents = useCallback(() => {
        const socket = ref.current.socket;
        if (!socket) return;
        
        if (debug) { consola.info('[Sockets] BIND SYSTEM EVENTS'); }
        
        socket.on('connect', () => {
            if (debug) { consola.info('[Sockets] connected, reattaching listeners...'); }
            ref.current.isConnected = true;
            
            // ВАЖНО: При каждом удачном (пере)подключении восстанавливаем подписки компонентов!
            reattachListeners();
            setConnectionLost(false);
            setTimeout(() => setShowedLoader(false), 2_000);
        });
        
        socket.on('connect_error', (err) => {
            consola.error('[Sockets] connect_error', err);
            ref.current.isConnected = false;
            setConnectionLost(true);
            setShowedLoader(true);
        });
        
        socket.on('disconnect', (reason) => {
            consola.error('[Sockets] disconnected', reason);
            ref.current.isConnected = false;
            if (reason !== 'io client disconnect') {
                setConnectionLost(true);
                setShowedLoader(true);
            }
        });
    }, [ reattachListeners ]);
    
    const connect = useCallback((userId: string) => {
        if (ref.current.socket?.connected) {
            consola.info('[Sockets] already connected');
            return;
        }
        
        // Если сокет уже создан, просто вызываем connect() без пересоздания объекта
        if (ref.current.socket) {
            ref.current.socket.connect();
            return;
        }
        
        if (debug) { consola.info('[Sockets] connect', userId); }
        ref.current.userId = userId;
        const token = localStorage.getItem('session');
        
        const socket = io(
            import.meta.env.DEV ? 'http://localhost:8051/notifications' : 'https://quack327.spy-duck.com/notifications',
            {
                auth: { token },
                transports: [ 'websocket' ],
                autoConnect: false,
                reconnection: true,
                reconnectionDelay: 500,
                reconnectionDelayMax: 5000,
                reconnectionAttempts: import.meta.env.DEV ? 1 : 100,
                timeout: 20_000,
                withCredentials: true,
            },
        );
        
        ref.current.socket = socket;
        bindEvents();
        socket.connect();
    }, [ bindEvents ]);
    
    const disconnect = useCallback(() => {
        if (debug) { consola.info('[Sockets] disconnect (explicit)'); }
        if (ref.current.socket) {
            ref.current.socket.disconnect();
            ref.current.socket = null;
        }
    }, []);
    
    const subscribe = useCallback((event: SocketEvent, callback: (data: any) => void) => {
        if (debug) { consola.log('[Sockets] subscribe', event); }
        
        // 1. Сохраняем подписку в реестр в любом случае (даже если сокет ещё не подключен)
        if (!listenersRef.current.has(event)) {
            listenersRef.current.set(event, new Set());
        }
        listenersRef.current.get(event)!.add(callback);
        
        // 2. Если сокет уже есть — вешаем слушатель на него
        if (ref.current.socket) {
            ref.current.socket.off(event, callback);
            ref.current.socket.on(event, callback);
        }
    }, []);
    
    const unsubscribe = useCallback((event: SocketEvent, callback: (data: any) => void) => {
        if (debug) { consola.debug('[Sockets] unsubscribe', event); }
        
        // 1. Удаляем из реестра
        if (listenersRef.current.has(event)) {
            listenersRef.current.get(event)!.delete(callback);
        }
        
        // 2. Удаляем с инстанса сокета
        if (ref.current.socket) {
            ref.current.socket.off(event, callback);
        }
    }, []);
    
    const contextValue = useMemo(() => ({
        connect,
        disconnect,
        subscribe,
        unsubscribe,
    }), [ connect, disconnect, subscribe, unsubscribe ]);
    
    return (
        <SocketsContext value={ contextValue }>
            { children }
            <Affix position={ { bottom: 20, left: 20 } } h={ 28 }>
                <Transition transition='slide-up' mounted={ showedLoader }>
                    { (transitionStyles) => (
                        <Tooltip
                            label={ connectionLost
                                ? 'Соединение потеряно. Повторное подключение'
                                : 'Соединение установлено' }
                            position='left'
                        >
                            <ActionIcon
                                color='gray'
                                radius='xl'
                                style={ transitionStyles }
                                h={ 28 }
                            >
                                { connectionLost && (<Loader size={ 19 } color='red'/>) }
                                { !connectionLost && (<IconCircleCheck color='green'/>) }
                            </ActionIcon>
                        </Tooltip>
                    ) }
                </Transition>
            </Affix>
        
        </SocketsContext>
    );
};